import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Flame } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTeamRole } from "@/hooks/useTeamRole";
import { logActivity } from "@/lib/staffActivity";
import { toast } from "sonner";
import type { DbLead } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staffId: string;
  staffName: string;
  leads: DbLead[];           // pool to choose from (typically unassigned)
  onAssigned?: () => void;
}

function tierColor(score: number) {
  if (score >= 85) return "bg-hot/10 text-hot";
  if (score >= 50) return "bg-warning/10 text-warning";
  return "bg-muted text-muted-foreground";
}

export default function LeadAssignmentModal({ open, onOpenChange, staffId, staffName, leads, onAssigned }: Props) {
  const { user } = useAuth();
  const { agencyId } = useTeamRole();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return leads;
    const q = search.toLowerCase();
    return leads.filter(l =>
      l.name.toLowerCase().includes(q) ||
      (l.zip_code || "").includes(q) ||
      (l.acuity_notes || "").toLowerCase().includes(q)
    );
  }, [leads, search]);

  const allSelected = filtered.length > 0 && filtered.every(l => selected.has(l.id));

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(l => next.delete(l.id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(l => next.add(l.id));
        return next;
      });
    }
  }

  async function handleAssign() {
    if (selected.size === 0 || !user || !agencyId) return;
    setSaving(true);
    try {
      const ids = Array.from(selected);
      const { error: updErr } = await supabase
        .from("leads")
        .update({ assigned_to: staffId })
        .in("id", ids);
      if (updErr) throw updErr;

      const assignmentRows = ids.map(id => ({
        lead_id: id,
        assigned_to: staffId,
        assigned_by: user.id,
        note: note || null,
      }));
      const { error: insErr } = await supabase.from("lead_assignments").insert(assignmentRows);
      if (insErr) throw insErr;

      ids.forEach(id => logActivity(user.id, agencyId, "lead_assigned", id));

      toast.success(`Assigned ${ids.length} lead${ids.length > 1 ? "s" : ""} to ${staffName}`);
      setSelected(new Set());
      setNote("");
      onAssigned?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to assign leads");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="text-navy text-lg">Assign Leads to {staffName}</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search leads by name, ZIP, or notes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No leads available to assign.</div>
          ) : (
            <>
              <label className="flex items-center gap-2 py-2 border-b mb-1 cursor-pointer">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Select All ({filtered.length})</span>
              </label>
              {filtered.map(lead => {
                const checked = selected.has(lead.id);
                return (
                  <label
                    key={lead.id}
                    className={`flex items-center gap-3 py-2.5 px-2 rounded cursor-pointer border-b last:border-b-0 hover:bg-muted/40 ${checked ? "bg-gold/5" : ""}`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggle(lead.id)} />
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <span className="font-medium text-navy text-sm truncate flex items-center gap-1.5">
                        {lead.score === 100 && <Flame className="h-3.5 w-3.5 text-gold" />}
                        {lead.name}
                      </span>
                      <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tierColor(lead.score)}`}>
                        {lead.score}
                      </span>
                      {lead.acuity_notes && (
                        <span className="shrink-0 hidden sm:inline text-[11px] text-muted-foreground truncate">· {lead.acuity_notes}</span>
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </span>
                  </label>
                );
              })}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Note for {staffName} (optional)
            </label>
            <Textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={`Add a note about these leads...`}
              rows={2}
              className="resize-none"
            />
          </div>
          <Button
            onClick={handleAssign}
            disabled={selected.size === 0 || saving}
            className="w-full bg-gold text-navy hover:bg-gold/90"
          >
            {saving ? "Assigning..." : `Assign ${selected.size} Lead${selected.size === 1 ? "" : "s"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
